import React, { useEffect, useState } from 'react';
import './App.css';
import PixelSprite, { LogoMark, sprites } from './PixelSprite';
import SpaceScene from './SpaceScene';

const CONTACT_EMAIL = 'inquiries@vlgi.org';

const projects = [
  {
    name: 'Beacon',
    url: 'https://trybeacon.chat/',
    domain: 'trybeacon.chat',
    status: 'Live',
    sprite: 'beacon',
    tone: 'yellow',
    description: 'A fast, intimate voice-calling app for groups: send a beacon, and the first person who answers joins a live 1:1 audio call.'
  },
  {
    name: 'laboratory.love',
    url: 'https://laboratory.love/',
    domain: 'laboratory.love',
    status: 'Live',
    sprite: 'flask',
    tone: 'red',
    description: 'Helps consumers understand what is actually in the packaged foods and everyday products they use.'
  },
  {
    name: 'Timeline',
    status: 'Mysterious',
    sprite: 'hourglass',
    tone: 'blue',
    description: 'A decentralized interactive archive of human history.'
  }
];

const principles = [
  'Move fast and create things that should already exist.',
  'Run parallel experiments across multiple domains.',
  'Tools that normal people actually want to use.',
  'Markets where slow-moving incumbents create opportunities for step-function improvements.'
];

const inventory = [
  'Consumer AI',
  'Distributed systems',
  'Novel organizational structures',
  'Fast experiments',
  'Consumer tools',
  'Research direction'
];

const getInitialTheme = () => {
  const storedTheme = window.localStorage.getItem('vlgi-theme');

  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const ProjectCard = ({ project }) => {
  const isLive = Boolean(project.url);
  const Tag = isLive ? 'a' : 'article';
  const linkProps = isLive ? { href: project.url } : {};

  return (
    <li className="cartridge-slot">
      <Tag className={`cartridge cartridge-${project.tone}`} {...linkProps}>
        <div className="cartridge-label">
          <PixelSprite className="cartridge-sprite" {...sprites[project.sprite]} />
          <span className={isLive ? 'status status-live' : 'status status-unknown'}>
            {project.status}
          </span>
        </div>
        <div className="cartridge-body">
          <h3>{project.name}</h3>
          <p>{project.description}</p>
          <span className="cartridge-cta">
            {isLive ? `Visit ${project.domain}` : 'Details pending'}
          </span>
        </div>
      </Tag>
    </li>
  );
};

const App = () => {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('vlgi-theme', theme);

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute('content', theme === 'dark' ? '#14151f' : '#f4efe2');
    }
  }, [theme]);

  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <div className="vlgi-shell">
      <header className="site-header">
        <a className="wordmark" href="/" aria-label="VLGI home">
          <LogoMark className="wordmark-logo" />
          <span>VLGI</span>
        </a>
        <button
          className="pixel-button theme-toggle"
          type="button"
          onClick={() => setTheme(nextTheme)}
          aria-label={`Switch to ${nextTheme} mode`}
        >
          <PixelSprite className="theme-icon" {...sprites[theme === 'dark' ? 'sun' : 'moon']} />
          <span aria-hidden="true">{nextTheme === 'dark' ? 'Dark' : 'Light'}</span>
        </button>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <h1 id="hero-title">Very Little Gravitas Indeed</h1>
            <p className="hero-lede">A holding company for experimental business units.</p>
            <p className="hero-text">
              VLGI builds and supports fast-moving experiments across consumer AI,
              distributed systems, and new models for alignment and ownership.
            </p>
            <div className="hero-actions">
              <a className="pixel-button pixel-button-primary" href="#projects">See the projects</a>
              <a className="pixel-button" href={`mailto:${CONTACT_EMAIL}`}>Email us</a>
            </div>
          </div>

          <div className="hero-viewport">
            <SpaceScene />
          </div>
        </section>

        <section className="section" id="projects" aria-labelledby="projects-title">
          <div className="section-intro">
            <h2 id="projects-title">Current initiatives</h2>
            <p>Small bets, shipped quickly. Two are live; one is still under wraps.</p>
          </div>
          <ul className="cartridge-grid">
            {projects.map((project) => (
              <ProjectCard project={project} key={project.name} />
            ))}
          </ul>
        </section>

        <section className="section approach" aria-label="Approach">
          <div className="approach-column">
            <h2 id="principles-title">Operating principles</h2>
            <ul className="principle-list" aria-labelledby="principles-title">
              {principles.map((principle) => (
                <li key={principle}>{principle}</li>
              ))}
            </ul>
          </div>
          <div className="approach-column">
            <h2 id="inventory-title">Inventory</h2>
            <ul className="inventory-list" aria-labelledby="inventory-title">
              {inventory.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="contact" aria-labelledby="contact-title">
          <h2 id="contact-title">Building something that should already exist?</h2>
          <p>We like hearing about new experiments, partnerships, and odd ideas.</p>
          <a className="pixel-button contact-button" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
        </section>
      </main>

      <footer className="site-footer">
        <LogoMark className="footer-logo" />
        <span>© {new Date().getFullYear()} Very Little Gravitas Indeed, LLC</span>
      </footer>
    </div>
  );
};

export default App;
